import { IonModal } from "@ionic/react";
import { useFormik } from "formik";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TextField } from "../../components/atoms/LoginTextField";
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalActions,
  ModalFields,
  ModalPrimaryButton,
  ModalQuietButton,
  ModalError,
} from "../../components/atoms/ModalKit";
import * as Yup from "yup";
import { forgotPassword } from "../../services/user.service";
import { useConfig } from "../../contexts/ConfigContext";
import Icon from "../../components/atoms/Icon";
import { alertCircle } from "ionicons/icons";
import useToast from "../../hooks/useToast";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
}

interface forgotPasswordFormType {
  login: string;
}

// const forgotPasswordSchema = Yup.object().shape({
//     login: Yup.string().email().required(),
// });

const frenchMobileRegExp = /^(06|07)[0-9]{8}$/;

const forgotPasswordSchema = Yup.object().shape({
  login: Yup.mixed()
    .test(
      'is-email-or-french-mobile',
      'Input must be a valid email or French mobile number',
      value => Yup.string().email().isValidSync(value) || frenchMobileRegExp.test(value)
    )
    .required('Email or Phone is required'),
});

const ForgotPwdModal: FC<Props> = ({ isOpen, onDismiss }) => {
  const { t } = useTranslation();
  const { study } = useConfig();
  const [isError, setIsError] = useState(false);
  const present = useToast();

  const handleSubmit = async (values: forgotPasswordFormType) => {
    try {
      setIsError(false);
      await forgotPassword(
            {
            studyId: study.id,
            },
            {
            login: values.login,
            }
      );
      onDismiss();
    } catch (e) {
      setIsError(true);
      present(e?.response?.data?.message);
    }
  };

  const { values, touched, errors, submitForm, setFieldValue } =
    useFormik<forgotPasswordFormType>({
      initialValues: {
        login: "",
      },
      onSubmit: handleSubmit,
      validationSchema: forgotPasswordSchema,
    });

  return (
    <IonModal isOpen={isOpen} backdropDismiss={true} onWillDismiss={onDismiss}>
      <ModalOverlay onClick={onDismiss}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{t("forgot_pwd_title")}</ModalTitle>

        <ModalFields>
          <TextField
            value={values.login}
            type="text"
            error={touched.login && errors.login}
            onChange={(value) => setFieldValue("login", value)}
            label={t("forgot_pwd_email_phone")}
            placeholder={t("forgot_pwd_email_phone")}
          />
        </ModalFields>

        {isError && (
          <ErrorRow>
            <Icon icon={alertCircle} />
            {t("forgot_pwd_error")}
          </ErrorRow>
        )}
        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>
            {t("common_cancel")}
          </ModalQuietButton>
          <ModalPrimaryButton onClick={submitForm}> {t("forgot_pwd_submit")}</ModalPrimaryButton>
        </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </IonModal>
  );
};

/** Kit error line, laid out as a flex row so the alert icon sits on the text. */
const ErrorRow = styled(ModalError)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export default ForgotPwdModal;
